import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Folder, 
  FileText, 
  MoreVertical, 
  Edit3, 
  Trash2, 
  Download,
  Clock,
  ChevronUp,
  FolderOpen,
  Edit,
  X,
  Users,
  ChevronDown,
  ArrowUpDown,
  Eye,
  Calendar
} from 'lucide-react';
import AuditTrailModal from './AuditTrailModal';
import { API_URL } from '../utils/apiConfig';

// Apple-inspired color palette from landing page
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

// Global font styles from landing page
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const GitHubStyleFileList = ({
  documents = [],
  folders = [],
  isLoading = false,
  isFetching = false,
  error = null,
  activeTab = 'uploaded',
  userId,
  currentFolderId = null,
  onFolderSelect,
  onDeleteDocument,
  onMoveDocument,
  onDeleteFolder,
  onShowSignatureFields,
  statusFilter = 'all',
  viewMode = 'grid'
}) => {
  const navigate = useNavigate();
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [navigating, setNavigating] = useState(false);
  const [dragOverRow, setDragOverRow] = useState(null);
  
  // Sorting state - Default to most recent first
  const [sortField, setSortField] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Audit trail modal state
  const [showAuditTrail, setShowAuditTrail] = useState({ show: false, documentId: null, documentName: null });

  // Reset navigation state when component unmounts or user changes
  useEffect(() => {
    return () => {
      setNavigating(false);
    };
  }, []);

  // Reset navigation state when documents or activeTab changes
  useEffect(() => {
    if (navigating) {
      const timer = setTimeout(() => {
        setNavigating(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [documents, activeTab, navigating]);

  // Helper functions for document ownership
  const isOwnedByUser = useCallback((doc) => {
    return (doc.ownerId === userId) || 
      (doc.owner?.id === userId) || 
      (doc.documentOwner?.id === userId);
  }, [userId]);

  const isSharedWithUser = useCallback((doc) => {
    return (doc.ownerId !== userId) &&
      (doc.owner?.id !== userId) &&
      (doc.documentOwner?.id !== userId) &&
      doc.permission !== 'owner';
  }, [userId]);

  // Filter documents based on active tab and status
  const filteredDocuments = useMemo(() => {
    // Ensure documents is an array
    const documentsArray = Array.isArray(documents) ? documents : [];
    let filtered = documentsArray;
    
    // Filter by tab (ownership)
    if (activeTab === 'shared') {
      filtered = documentsArray.filter(doc => isSharedWithUser(doc));
    } else {
      filtered = documentsArray.filter(doc => isOwnedByUser(doc));
    }
    
    // Filter by status (canonical values only). Prefer server-filtered results;
    // keep this as a safeguard if backend returns mixed statuses.
    if (statusFilter !== 'all') {
      filtered = filtered.filter(doc => {
        if (statusFilter === 'pending_signature') return doc.status === 'pending_signature';
        if (statusFilter === 'completed') return doc.status === 'completed';
        if (statusFilter === 'draft') return doc.status === 'draft';
        return doc.status === statusFilter;
      });
    }
    
    // Filter by current folder
    if (currentFolderId !== null) {
      filtered = filtered.filter(doc => doc.folderId === currentFolderId);
    } else {
      filtered = filtered.filter(doc => doc.folderId === null || doc.folderId === undefined);
    }
    
    return filtered;
  }, [documents, activeTab, isOwnedByUser, isSharedWithUser, statusFilter, currentFolderId]);

  // Combine and sort folders and documents
  const allItems = useMemo(() => {
    const items = [];
    
    // Add parent folder navigation if we're in a subfolder
    if (currentFolderId !== null) {
      items.push({
        type: 'parent',
        id: 'parent',
        name: '..',
        isFolder: true
      });
    }
    
    // Add folders
    const sortedFolders = [...folders].sort((a, b) => {
      if (sortField === 'name') {
        const result = a.name.localeCompare(b.name);
        return sortDirection === 'asc' ? result : -result;
      }
      if (sortField === 'updatedAt') {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        const result = dateA - dateB;
        return sortDirection === 'asc' ? result : -result;
      }
      return 0;
    });
    
    sortedFolders.forEach(folder => {
      items.push({
        type: 'folder',
        id: folder.id,
        name: folder.name,
        isFolder: true,
        createdAt: folder.createdAt,
        updatedAt: folder.updatedAt
      });
    });
    
    // Add documents
    const sortedDocuments = [...filteredDocuments].sort((a, b) => {
      if (sortField === 'name') {
        const result = a.name.localeCompare(b.name);
        return sortDirection === 'asc' ? result : -result;
      }
      if (sortField === 'updatedAt') {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        const result = dateA - dateB;
        return sortDirection === 'asc' ? result : -result;
      }
      if (sortField === 'fileSize') {
        const sizeA = a.fileSize || 0;
        const sizeB = b.fileSize || 0;
        const result = sizeA - sizeB;
        return sortDirection === 'asc' ? result : -result;
      }
      return 0;
    });
    
    // Remove duplicates by document ID
    const seenDocumentIds = new Set();
    
    sortedDocuments.forEach(doc => {
      if (!seenDocumentIds.has(doc.id)) {
        seenDocumentIds.add(doc.id);
        items.push({
          type: 'document',
          id: doc.id,
          name: doc.name,
          isFolder: false,
          status: doc.status,
          fileType: doc.fileType,
          fileSize: doc.fileSize,
          createdAt: doc.createdAt,
          updatedAt: doc.updatedAt,
          owner: doc.owner,
          ownerId: doc.ownerId,
          permissionUsers: doc.permissionUsers,
          recipient: doc.recipient,
          previewUrl: doc.previewUrl,
          previewGenerated: doc.previewGenerated
        });
      }
    });
    
    return items;
  }, [folders, filteredDocuments, currentFolderId, sortField, sortDirection]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffTime = nowOnly - dateOnly;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return `${Math.ceil(diffDays / 365)} years ago`;
  };

  // Get file type icon
  const getFileTypeIcon = (fileType, size = 20) => {
    if (!fileType) return <FileText size={size} />;
    
    if (fileType.includes('pdf')) {
      return <FileText size={size} className="text-red-500" />;
    }
    if (fileType.includes('image')) {
      return <FileText size={size} className="text-green-500" />;
    }
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) {
      return <FileText size={size} className="text-green-600" />;
    }
    return <FileText size={size} className="text-blue-600" />;
  };

  // Get file type badge
  const getFileTypeBadge = (fileType) => {
    if (!fileType) return null;
    
    let label = 'DOC';
    let color = colors.gray500;
    
    if (fileType.includes('pdf')) {
      label = 'PDF';
      color = '#dc2626';
    } else if (fileType.includes('image')) {
      label = 'IMG';
      color = '#059669';
    } else if (fileType.includes('spreadsheet') || fileType.includes('excel')) {
      label = 'XLS';
      color = '#16a34a';
    } else if (fileType.includes('word') || fileType.includes('doc')) {
      label = 'DOC';
      color = '#2563eb';
    }
    
    return (
      <span 
        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
        style={{
          backgroundColor: `${color}20`,
          color: color,
          ...fontStyles.medium
        }}
      >
        {label}
      </span>
    );
  };

  // Render document preview
  const renderDocumentPreview = (item) => {
    if (item.isFolder || item.type === 'parent') {
      return (
        <div 
          className="w-full h-48 flex items-center justify-center rounded-t-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
            border: `1px solid ${colors.gray200}`
          }}
        >
          {item.type === 'parent' ? (
            <ChevronUp size={32} style={{color: colors.gray400}} />
          ) : (
            <Folder size={32} style={{color: colors.accent}} />
          )}
        </div>
      );
    }

    // Document preview
    if (item.previewGenerated && item.previewUrl) {
      // Import the preview utility (should be imported at top level in real implementation)
      // For now, inline the URL construction
      const token = localStorage.getItem('token');
      const previewUrl = `${API_URL}/documents/${item.id}/preview${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      
      return (
        <div className="w-full h-48 rounded-t-xl overflow-hidden relative group bg-white border-b border-gray-100">
          <img 
            src={previewUrl}
            alt={`Preview of ${item.name}`}
            className="w-full h-full object-cover"
            style={{ 
              objectPosition: 'center top',
              imageRendering: 'crisp-edges',
              imageRendering: '-webkit-optimize-contrast',
              imageRendering: 'pixelated'
            }}
            onLoad={(e) => {
              // Enhanced image quality settings
              e.target.style.imageRendering = 'high-quality';
              e.target.style.imageRendering = '-webkit-optimize-contrast';
              e.target.style.imageRendering = 'crisp-edges';
              e.target.style.filter = 'contrast(1.05) brightness(1.02)';
            }}
            onError={(e) => {
              // Fallback if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className="w-full h-full hidden items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)',
              border: `1px solid ${colors.gray200}`
            }}
          >
            {getFileTypeIcon(item.fileType, 32)}
          </div>
          {/* Overlay with file type badge */}
          <div className="absolute top-2 right-2">
            {getFileTypeBadge(item.fileType)}
          </div>
        </div>
      );
    }

    // Fallback preview
    return (
      <div 
        className="w-full h-48 flex flex-col items-center justify-center rounded-t-xl relative"
        style={{
          background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)',
          border: `1px solid ${colors.gray200}`
        }}
      >
        {getFileTypeIcon(item.fileType, 32)}
        <span className="text-xs text-gray-400 mt-1" style={fontStyles.body}>
          No preview
        </span>
        {/* File type badge */}
        <div className="absolute top-2 right-2">
          {getFileTypeBadge(item.fileType)}
        </div>
      </div>
    );
  };

  // Render compact preview for list view
  const renderCompactPreview = (item) => {
    if (item.isFolder || item.type === 'parent') {
      return (
        <div 
          className="w-10 h-8 flex items-center justify-center rounded-md"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
            border: `1px solid ${colors.gray200}`
          }}
        >
          {item.type === 'parent' ? (
            <ChevronUp size={16} style={{color: colors.gray400}} />
          ) : (
            <Folder size={16} style={{color: colors.accent}} />
          )}
        </div>
      );
    }

    // Document preview
    if (item.previewGenerated && item.previewUrl) {
      const token = localStorage.getItem('token');
      const previewUrl = `${API_URL}/documents/${item.id}/preview${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      
      return (
        <div className="w-10 h-8 rounded-md overflow-hidden relative">
          <img 
            src={previewUrl}
            alt={`Preview of ${item.name}`}
            className="w-full h-full object-cover object-top"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className="w-full h-full hidden items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)',
              border: `1px solid ${colors.gray200}`
            }}
          >
            {getFileTypeIcon(item.fileType, 16)}
          </div>
        </div>
      );
    }

    // Fallback preview
    return (
      <div 
        className="w-10 h-8 flex items-center justify-center rounded-md"
        style={{
          background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)',
          border: `1px solid ${colors.gray200}`
        }}
      >
        {getFileTypeIcon(item.fileType, 16)}
      </div>
    );
  };

  // Render recipients for a document
  const renderRecipients = (document) => {
    const getDocumentRecipients = (doc) => {
      if (doc.permissionUsers && doc.permissionUsers.length > 0) {
        return doc.permissionUsers;
      }
      if (doc.recipient) {
        return [{
          name: doc.recipient.split('@')[0],
          email: doc.recipient
        }];
      }
      return [];
    };

    const recipients = getDocumentRecipients(document);
    
    if (recipients.length === 0) {
      return (
        <div 
          className="flex items-center text-sm"
          style={{
            ...fontStyles.body,
            color: colors.gray500,
            letterSpacing: '-0.005em'
          }}
        >
          <Users size={14} className="mr-1.5" />
          <span>No recipients</span>
        </div>
      );
    }

    if (recipients.length === 1) {
      const recipient = recipients[0];
      return (
        <div className="flex items-center">
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-medium"
            style={{
              backgroundColor: colors.accentLight,
              color: colors.accent
            }}
          >
            {recipient.name?.charAt(0)?.toUpperCase() || recipient.email?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="min-w-0 flex-1">
            <div 
              className="text-sm truncate"
              style={{
                ...fontStyles.medium,
                color: colors.gray900,
                letterSpacing: '-0.01em'
              }}
            >
              {recipient.name || 'User'}
            </div>
            <div 
              className="text-xs truncate"
              style={{
                ...fontStyles.body,
                color: colors.gray500,
                letterSpacing: '-0.005em'
              }}
            >
              {recipient.email}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center">
        <div className="flex -space-x-1 mr-2">
          {recipients.slice(0, 3).map((recipient, index) => (
            <div 
              key={index}
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border-2 border-white"
              style={{
                backgroundColor: colors.accentLight,
                color: colors.accent
              }}
            >
              {recipient.name?.charAt(0)?.toUpperCase() || recipient.email?.charAt(0)?.toUpperCase() || '?'}
            </div>
          ))}
          {recipients.length > 3 && (
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border-2 border-white"
              style={{
                backgroundColor: colors.gray200,
                color: colors.gray700
              }}
            >
              +{recipients.length - 3}
            </div>
          )}
        </div>
        <div 
          className="text-sm"
          style={{
            ...fontStyles.body,
            color: colors.gray500,
            letterSpacing: '-0.005em'
          }}
        >
          {recipients.length} recipient{recipients.length > 1 ? 's' : ''}
        </div>
      </div>
    );
  };

  // Render compact recipients for list view
  const renderCompactRecipients = (document) => {
    const getDocumentRecipients = (doc) => {
      if (doc.permissionUsers && doc.permissionUsers.length > 0) {
        return doc.permissionUsers;
      }
      if (doc.recipient) {
        return [{
          name: doc.recipient.split('@')[0],
          email: doc.recipient
        }];
      }
      return [];
    };

    const recipients = getDocumentRecipients(document);
    
    if (recipients.length === 0) {
      return (
        <div 
          className="flex items-center text-xs"
          style={{
            ...fontStyles.body,
            color: colors.gray500,
            letterSpacing: '-0.005em'
          }}
        >
          <Users size={12} className="mr-1.5" />
          <span>None</span>
        </div>
      );
    }

    return (
      <div className="flex items-center">
        <div className="flex -space-x-1 mr-2">
          {recipients.slice(0, 2).map((recipient, index) => (
            <div 
              key={index}
              className="w-5 h-5 rounded-full flex items-center justify-center border border-white"
              style={{
                backgroundColor: colors.accentLight,
                color: colors.accent,
                fontSize: '10px',
                ...fontStyles.medium
              }}
            >
              {recipient.name?.charAt(0)?.toUpperCase() || recipient.email?.charAt(0)?.toUpperCase() || '?'}
            </div>
          ))}
          {recipients.length > 2 && (
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center border border-white"
              style={{
                backgroundColor: colors.gray200,
                color: colors.gray700,
                fontSize: '9px',
                ...fontStyles.medium
              }}
            >
              +{recipients.length - 2}
            </div>
          )}
        </div>
        <div 
          className="text-xs"
          style={{
            ...fontStyles.body,
            color: colors.gray500,
            letterSpacing: '-0.005em'
          }}
        >
          {recipients.length}
        </div>
      </div>
    );
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': {
        bg: colors.successLight,
        text: colors.success,
        label: 'Completed'
      },
      'pending_signature': {
        bg: colors.warningLight,
        text: colors.warning,
        label: 'Pending'
      },
      'waiting_for_signature': {
        bg: colors.warningLight,
        text: colors.warning,
        label: 'Pending'
      },
      'draft': {
        bg: colors.gray100,
        text: colors.gray700,
        label: 'Draft'
      },
      'voided': {
        bg: '#fee2e2', // red-100
        text: '#dc2626', // red-600
        label: 'Voided'
      }
    };

    const config = statusConfig[status] || statusConfig['draft'];
    
    return (
      <span 
        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs"
        style={{
          backgroundColor: config.bg,
          color: config.text,
          ...fontStyles.medium,
          letterSpacing: '-0.005em'
        }}
      >
        {config.label}
      </span>
    );
  };

  // Get compact status badge for list view
  const getCompactStatusBadge = (status) => {
    const statusConfig = {
      'completed': {
        bg: colors.successLight,
        text: colors.success,
        label: 'Completed',
        dot: colors.success
      },
      'pending_signature': {
        bg: colors.warningLight,
        text: colors.warning,
        label: 'Pending',
        dot: colors.warning
      },
      'waiting_for_signature': {
        bg: colors.warningLight,
        text: colors.warning,
        label: 'Pending',
        dot: colors.warning
      },
      'draft': {
        bg: colors.gray100,
        text: colors.gray700,
        label: 'Draft',
        dot: colors.gray500
      },
      'voided': {
        bg: '#fee2e2', // red-100
        text: '#dc2626', // red-600
        label: 'Voided',
        dot: '#dc2626' // red-600
      }
    };

    const config = statusConfig[status] || statusConfig['draft'];
    
    return (
      <span 
        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
        style={{
          backgroundColor: config.bg,
          color: config.text,
          ...fontStyles.medium
        }}
      >
        <div 
          className="w-1.5 h-1.5 rounded-full mr-1.5"
          style={{ backgroundColor: config.dot }}
        />
        {config.label}
      </span>
    );
  };

  // Handle item click
  const handleItemClick = (item, event) => {
    if (event.defaultPrevented) return;
    
    if (item.type === 'parent') {
      onFolderSelect(null);
    } else if (item.isFolder) {
      onFolderSelect(item.id);
    } else {
      setNavigating(true);
      navigate(`/document/${item.id}`);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    if (item.type === 'parent') {
      e.preventDefault();
      return;
    }
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    // Set the document ID in the drag data transfer for breadcrumb drops
    e.dataTransfer.setData('text/plain', item.id);
  };

  const handleDragOver = (e, item) => {
    if (item.isFolder && draggedItem && draggedItem.id !== item.id) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (viewMode === 'grid') {
        setDragOverFolder(item.id);
      } else {
        setDragOverRow(item.id);
      }
    }
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
    setDragOverRow(null);
  };

  const handleDrop = async (e, targetFolder) => {
    e.preventDefault();
    setDragOverFolder(null);
    setDragOverRow(null);
    
    if (draggedItem && targetFolder.isFolder && onMoveDocument) {
      try {
        // Handle special case for parent folder navigation
        if (targetFolder.type === 'parent') {
          // Find the parent folder ID based on current folder structure
          const currentFolder = folders.find(f => f.id === currentFolderId);
          const parentFolderId = currentFolder ? currentFolder.parentId : null;
          
          await onMoveDocument(draggedItem.id, parentFolderId);
        } else {
          // Normal folder drop
          await onMoveDocument(draggedItem.id, targetFolder.id);
        }
      } catch (error) {
        console.error('Error moving document:', error);
      }
    }
    setDraggedItem(null);
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = "" }) => {
    const isActive = sortField === field;
    const canSort = ['name', 'updatedAt', 'fileSize'].includes(field);
    
    if (!canSort) {
      return <div className={className}>{children}</div>;
    }

    return (
      <button
        onClick={() => handleSort(field)}
        className={`${className} flex items-center space-x-1.5 transition-colors duration-200`}
        style={{
          ...fontStyles.medium,
          color: isActive ? colors.gray900 : colors.gray700,
          letterSpacing: '-0.005em'
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.target.style.color = colors.gray900;
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.target.style.color = colors.gray700;
          }
        }}
      >
        <span>{children}</span>
        {isActive ? (
          sortDirection === 'asc' ? (
            <ChevronUp size={14} />
          ) : (
            <ChevronDown size={14} />
          )
        ) : (
          <ArrowUpDown size={12} style={{ opacity: 0.4 }} />
        )}
      </button>
    );
  };

  // Show big spinner only when there is truly no data yet (cold load)
  if (isLoading && (!documents || documents.length === 0) && (!folders || folders.length === 0)) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm" style={{boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)'}}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{borderTopColor: colors.accent, borderBottomColor: colors.accent}}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" style={{boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)'}}>
        <div className="text-center">
          <div className="text-red-500 mb-2">⚠️</div>
          <h3 className="font-medium text-red-800" style={fontStyles.medium}>Error loading content</h3>
          <p className="text-red-600 text-sm" style={fontStyles.body}>{error.message || 'Failed to load. Please try again.'}</p>
        </div>
      </div>
    );
  }

    return (
    <div style={{fontFamily: fontStyles.fontFamily}}>
      {/* Small syncing pill when background refetch is in progress */}
      {isFetching && !isLoading && (
        <div className="mb-3 flex items-center text-xs text-gray-500">
          <div className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ backgroundColor: colors.accent }} />
          Syncing latest changes...
        </div>
      )}
      {/* Sorting Controls */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <SortableHeader field="name" className="text-sm">
            Sort by Name
          </SortableHeader>
          <SortableHeader field="updatedAt" className="text-sm">
            Sort by Date
          </SortableHeader>
          <SortableHeader field="fileSize" className="text-sm">
            Sort by Size
          </SortableHeader>
        </div>
        <div 
          className="text-sm" 
          style={{
            ...fontStyles.body,
            color: colors.gray500,
            letterSpacing: '-0.005em'
          }}
        >
          {allItems.length} item{allItems.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Content Display */}
      {allItems.length === 0 ? (
        <div 
          className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
          style={{boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)'}}
        >
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="mb-4" style={{color: colors.gray300}}>
              <Folder size={64} />
            </div>
            <h3 className="text-lg font-medium mb-2" style={{...fontStyles.heading, color: colors.gray900}}>
              No files or folders
            </h3>
            <p className="text-center max-w-md" style={{color: colors.gray500, ...fontStyles.body}}>
              {activeTab === 'shared' 
                ? 'No files have been shared with you yet.' 
                : 'Upload documents or create folders to organize your files.'}
            </p>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        // Grid View (existing card layout)
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {allItems.map((item) => (
            <div
              key={`${item.type}-${item.id}`}
              className={`bg-white rounded-xl border cursor-pointer transition-all duration-200 group ${
                dragOverFolder === item.id ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
              }`}
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                fontFamily: fontStyles.fontFamily,
                backgroundColor: dragOverFolder === item.id ? colors.accentLight : colors.white
              }}
              onMouseEnter={(e) => {
                if (dragOverFolder !== item.id) {
                  e.currentTarget.style.backgroundColor = colors.gray50;
                  e.currentTarget.style.borderColor = colors.gray300;
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (dragOverFolder !== item.id) {
                  e.currentTarget.style.backgroundColor = colors.white;
                  e.currentTarget.style.borderColor = colors.gray200;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
                }
              }}
              draggable={item.type !== 'parent'}
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={(e) => handleDragOver(e, item)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, item)}
              onClick={(event) => handleItemClick(item, event)}
            >
              {/* Document Preview Area */}
              {renderDocumentPreview(item)}

              {/* Card Content */}
              <div className="p-6">
                {/* Header with Title and Actions */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="font-semibold text-gray-900 truncate leading-tight"
                      style={{
                        ...fontStyles.heading,
                        fontSize: '16px',
                        lineHeight: '20px',
                        letterSpacing: '-0.01em'
                      }}
                      title={item.name}
                    >
                      {item.name}
                    </h3>
                    {!item.isFolder && item.fileSize && (
                      <p 
                        className="text-xs mt-1" 
                        style={{
                          ...fontStyles.body,
                          color: colors.gray500,
                          letterSpacing: '-0.005em'
                        }}
                      >
                        {formatFileSize(item.fileSize)}
                      </p>
                    )}
                  </div>

                  {/* Action Menu */}
                  {item.type !== 'parent' && (
                    <div className="flex items-center space-x-1 ml-2">
                      

                      {!item.isFolder && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/document/${item.id}`);
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                          title="View document"
                        >
                          <Eye size={16} />
                        </button>
                      )}
                      
                      {((item.ownerId === userId || item.owner?.id === userId) || item.isFolder) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.isFolder && onDeleteFolder) {
                              onDeleteFolder(item.id);
                            } else if (!item.isFolder && onDeleteDocument) {
                              onDeleteDocument(item.id, e);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                          title={item.isFolder ? "Delete folder" : "Delete document"}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Status and Recipients */}
                {!item.isFolder && item.type !== 'parent' && (
                  <div className="space-y-3 mb-4">
                    {/* Status */}
                    <div>
                      {getStatusBadge(item.status)}
                    </div>

                    {/* Recipients */}
                    <div>
                      {renderRecipients(item)}
                    </div>
                  </div>
                )}

                {/* Footer */}
                {item.type !== 'parent' && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div 
                      className="flex items-center text-xs" 
                      style={{
                        ...fontStyles.body,
                        color: colors.gray500,
                        letterSpacing: '-0.005em'
                      }}
                    >
                      <Calendar size={12} className="mr-1.5" />
                      {formatDate(item.createdAt)}
                    </div>
                    
                    {/* Right side content */}
                    <div className="flex items-center">
                      {/* Audit trail button for documents */}
                      {!item.isFolder && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAuditTrail({ 
                              show: true, 
                              documentId: item.id, 
                              documentName: item.name 
                            });
                          }}
                          className="px-2 py-1 text-xs hover:bg-gray-100 rounded-md transition-colors duration-200 flex items-center space-x-1"
                          style={{
                            ...fontStyles.body,
                            ...fontStyles.medium,
                            color: colors.gray500,
                            letterSpacing: '-0.01em'
                          }}
                          title="View audit trail"
                        >
                          <Clock size={11} />
                          <span>Audit trail</span>
                        </button>
                      )}
                      
                      {/* Folder label */}
                      {item.isFolder && (
                        <div 
                          className="text-xs" 
                          style={{
                            ...fontStyles.body,
                            ...fontStyles.medium,
                            color: colors.gray500,
                            letterSpacing: '-0.005em'
                          }}
                        >
                          Folder
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // List View (new table layout)
        <div 
          className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
          style={{
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            fontFamily: fontStyles.fontFamily
          }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100" style={{backgroundColor: colors.gray50}}>
                <th 
                  className="text-left py-4 px-5 text-sm"
                  style={{
                    ...fontStyles.medium,
                    color: colors.gray700,
                    letterSpacing: '-0.005em'
                  }}
                >
                  <SortableHeader field="name" className="flex items-center">
                    Name
                  </SortableHeader>
                </th>
                <th 
                  className="text-left py-4 px-5 text-sm hidden md:table-cell"
                  style={{
                    ...fontStyles.medium,
                    color: colors.gray700,
                    letterSpacing: '-0.005em'
                  }}
                >
                  Recipients
                </th>
                <th 
                  className="text-left py-4 px-5 text-sm hidden sm:table-cell"
                  style={{
                    ...fontStyles.medium,
                    color: colors.gray700,
                    letterSpacing: '-0.005em'
                  }}
                >
                  Status
                </th>
                <th 
                  className="text-left py-4 px-5 text-sm hidden lg:table-cell"
                  style={{
                    ...fontStyles.medium,
                    color: colors.gray700,
                    letterSpacing: '-0.005em'
                  }}
                >
                  <SortableHeader field="updatedAt" className="flex items-center">
                    Modified
                  </SortableHeader>
                </th>
                <th 
                  className="text-left py-4 px-5 text-sm hidden xl:table-cell"
                  style={{
                    ...fontStyles.medium,
                    color: colors.gray700,
                    letterSpacing: '-0.005em'
                  }}
                >
                  <SortableHeader field="fileSize" className="flex items-center">
                    Size
                  </SortableHeader>
                </th>
                <th 
                  className="text-right py-4 px-5 text-sm"
                  style={{
                    ...fontStyles.medium,
                    color: colors.gray700,
                    letterSpacing: '-0.005em'
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((item, index) => (
                <tr
                  key={`${item.type}-${item.id}`}
                  className="border-b border-gray-50 cursor-pointer group transition-all duration-200"
                  style={{
                    backgroundColor: dragOverRow === item.id 
                      ? colors.accentLight 
                      : index % 2 === 0 
                        ? colors.white 
                        : colors.gray50,
                    height: '68px'
                  }}
                  draggable={item.type !== 'parent'}
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDragOver={(e) => handleDragOver(e, item)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, item)}
                  onClick={(event) => handleItemClick(item, event)}
                  onMouseEnter={(e) => {
                    if (dragOverRow !== item.id) {
                      e.currentTarget.style.backgroundColor = colors.gray100;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (dragOverRow !== item.id) {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? colors.white : colors.gray50;
                    }
                  }}
                >
                  {/* Name column with preview */}
                  <td className="py-4 px-5">
                    <div className="flex items-center space-x-3">
                      {renderCompactPreview(item)}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 
                            className="text-sm truncate"
                            style={{
                              ...fontStyles.medium,
                              color: colors.gray900,
                              letterSpacing: '-0.01em'
                            }}
                            title={item.name}
                          >
                            {item.name}
                          </h3>
                          {!item.isFolder && item.fileType && (
                            <div className="flex-shrink-0">
                              {getFileTypeBadge(item.fileType)}
                            </div>
                          )}
                        </div>
                        {!item.isFolder && item.fileSize && (
                          <p 
                            className="text-xs mt-1 xl:hidden" 
                            style={{
                              ...fontStyles.body,
                              color: colors.gray500,
                              letterSpacing: '-0.005em'
                            }}
                          >
                            {formatFileSize(item.fileSize)}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Recipients column */}
                  <td className="py-4 px-5 hidden md:table-cell">
                    {!item.isFolder && item.type !== 'parent' ? (
                      renderCompactRecipients(item)
                    ) : (
                      <span 
                        className="text-xs"
                        style={{
                          color: colors.gray500,
                          ...fontStyles.body
                        }}
                      >
                        —
                      </span>
                    )}
                  </td>

                  {/* Status column */}
                  <td className="py-4 px-5 hidden sm:table-cell">
                    {!item.isFolder && item.type !== 'parent' ? (
                      getCompactStatusBadge(item.status)
                    ) : item.isFolder ? (
                      <span 
                        className="text-xs px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor: colors.gray100,
                          color: colors.gray700,
                          ...fontStyles.medium,
                          letterSpacing: '-0.005em'
                        }}
                      >
                        Folder
                      </span>
                    ) : (
                      <span 
                        className="text-xs"
                        style={{
                          color: colors.gray500,
                          ...fontStyles.body
                        }}
                      >
                        —
                      </span>
                    )}
                  </td>

                  {/* Modified column */}
                  <td className="py-4 px-5 hidden lg:table-cell">
                    {item.type !== 'parent' && (
                      <div 
                        className="flex items-center text-xs" 
                        style={{
                          ...fontStyles.body,
                          color: colors.gray500,
                          letterSpacing: '-0.005em'
                        }}
                      >
                        <Calendar size={12} className="mr-1.5" />
                        {formatDate(item.createdAt)}
                      </div>
                    )}
                  </td>

                  {/* Size column */}
                  <td className="py-4 px-5 hidden xl:table-cell">
                    {!item.isFolder && item.fileSize ? (
                      <span 
                        className="text-xs" 
                        style={{
                          ...fontStyles.body,
                          color: colors.gray500,
                          letterSpacing: '-0.005em'
                        }}
                      >
                        {formatFileSize(item.fileSize)}
                      </span>
                    ) : (
                      <span 
                        className="text-xs"
                        style={{
                          color: colors.gray500,
                          ...fontStyles.body
                        }}
                      >
                        —
                      </span>
                    )}
                  </td>

                  {/* Actions column */}
                  <td className="py-4 px-5 text-right">
                    {item.type !== 'parent' && (
                      <div className="flex items-center justify-end space-x-2">
                        {/* Standalone Audit Trail Button - only for documents */}
                        {!item.isFolder && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAuditTrail({ 
                                show: true, 
                                documentId: item.id, 
                                documentName: item.name 
                              });
                            }}
                            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 transition-all duration-200 flex items-center space-x-1.5"
                            style={{
                              ...fontStyles.medium,
                              color: colors.gray700,
                              letterSpacing: '-0.005em',
                              backgroundColor: colors.white
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = colors.gray50;
                              e.target.style.borderColor = colors.gray300;
                              e.target.style.color = colors.gray900;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = colors.white;
                              e.target.style.borderColor = colors.gray200;
                              e.target.style.color = colors.gray700;
                            }}
                            title="View audit trail"
                          >
                            <Clock size={12} />
                            <span>Audit trail</span>
                          </button>
                        )}

                        {/* 3-dot dropdown menu */}
                        <div className="relative opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(showMenu === item.id ? null : item.id);
                            }}
                            className="p-2 rounded-lg transition-colors duration-200"
                            style={{
                              color: colors.gray500,
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = colors.gray100;
                              e.target.style.color = colors.gray700;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = colors.gray500;
                            }}
                            title="More actions"
                          >
                            <MoreVertical size={14} />
                          </button>
                          
                          {/* Dropdown menu */}
                          {showMenu === item.id && (
                            <div 
                              className="absolute right-0 top-full mt-2 w-40 rounded-xl shadow-lg z-10 border border-gray-200 overflow-hidden"
                              style={{
                                backgroundColor: colors.white,
                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)',
                                fontFamily: fontStyles.fontFamily
                              }}
                            >
                              {!item.isFolder && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/document/${item.id}`);
                                    setShowMenu(null);
                                  }}
                                  className="w-full px-4 py-3 text-left text-sm flex items-center space-x-2.5 transition-colors duration-200"
                                  style={{
                                    ...fontStyles.medium,
                                    color: colors.gray700,
                                    letterSpacing: '-0.005em'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = colors.gray50;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <Eye size={14} />
                                  <span>View</span>
                                </button>
                              )}
                              
                              {((item.ownerId === userId || item.owner?.id === userId) || item.isFolder) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.isFolder && onDeleteFolder) {
                                      onDeleteFolder(item.id);
                                    } else if (!item.isFolder && onDeleteDocument) {
                                      onDeleteDocument(item.id, e);
                                    }
                                    setShowMenu(null);
                                  }}
                                  className="w-full px-4 py-3 text-left text-sm flex items-center space-x-2.5 transition-colors duration-200"
                                  style={{
                                    ...fontStyles.medium,
                                    color: colors.error,
                                    letterSpacing: '-0.005em'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = colors.errorLight;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <X size={14} />
                                  <span>Delete</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit Trail Modal */}
      {showAuditTrail.show && (
        <AuditTrailModal
          documentId={showAuditTrail.documentId}
          documentName={showAuditTrail.documentName}
          onClose={() => setShowAuditTrail({ show: false, documentId: null, documentName: null })}
        />
      )}
    </div>
  );
};

export default GitHubStyleFileList; 